import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { listQuarantine, quarantineBulkAction, listWriteOffs } from '../lib/api';
import { ShieldWarning, CheckCircle, Trash, ArrowUUpLeft, Package, Warning, Clock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

function StatCard({ label, value, accent }) {
  const colors = { slate: 'text-foreground', amber: 'text-amber-700', red: 'text-red-700', blue: 'text-brand' };
  return (
    <div className="border border-border bg-card p-4">
      <p className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">{label}</p>
      <p className={`text-3xl font-bold font-mono mt-1 ${colors[accent] || colors.slate}`}>{value ?? '—'}</p>
    </div>
  );
}

export default function QuarantineReview() {
  const [rows, setRows] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [writeOffs, setWriteOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null); // 'approve_for_sale' | 'write_off' | null
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [q, w] = await Promise.all([listQuarantine(), listWriteOffs()]);
      setRows(q.data.items || []); setTotalQty(q.data.total_quarantine_qty || 0);
      setWriteOffs(w.data || []);
    } catch (e) {
      toast.error('Failed to load quarantine queue');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allIds = useMemo(() => rows.map(r => r.inventory_id), [rows]);
  const allSelected = selectedIds.size === allIds.length && allIds.length > 0;

  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));
  const toggleOne = (id) => {
    const n = new Set(selectedIds);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelectedIds(n);
  };

  const openConfirm = (action) => {
    if (selectedIds.size === 0) { toast.error('Select at least one lot'); return; }
    setConfirmAction(action);
    setNote('');
  };

  const runAction = async () => {
    setBusy(true);
    try {
      const { data } = await quarantineBulkAction({
        inventory_ids: [...selectedIds],
        action: confirmAction,
        note: note || null,
      });
      const label = confirmAction === 'approve_for_sale' ? 'approved for sale' : 'written off';
      toast.success(`${data.processed} lot(s) ${label}${data.failed ? ` · ${data.failed} failed` : ''}`);
      setConfirmAction(null);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const selectedQty = useMemo(() =>
    rows.filter(r => selectedIds.has(r.inventory_id)).reduce((s, r) => s + r.quantity, 0),
    [rows, selectedIds]
  );

  return (
    <Layout>
      <div className="px-6 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldWarning size={18} weight="fill" className="text-amber-700" />
              <span className="text-[10px] font-bold tracking-[0.2em] text-amber-700 uppercase">Quality Triage</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Quarantine Review</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Triage returned items: release clean stock to <b>available</b> or <b>write off</b> unsalvageable lots.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openConfirm('approve_for_sale')} disabled={selectedIds.size === 0}
              data-testid="quar-approve-btn" className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50">
              <CheckCircle size={14} className="mr-1" /> Approve for sale
              {selectedIds.size > 0 && <span className="ml-1 font-mono">({selectedIds.size})</span>}
            </Button>
            <Button onClick={() => openConfirm('write_off')} disabled={selectedIds.size === 0} variant="outline"
              data-testid="quar-writeoff-btn" className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-50">
              <Trash size={14} className="mr-1" /> Write off
              {selectedIds.size > 0 && <span className="ml-1 font-mono">({selectedIds.size})</span>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Lots in quarantine" value={rows.length} accent="amber" />
          <StatCard label="Total units" value={totalQty} accent="amber" />
          <StatCard label="Selected" value={selectedIds.size} accent="blue" />
          <StatCard label="Selected units" value={selectedQty} accent="blue" />
        </div>

        <Tabs defaultValue="queue">
          <TabsList>
            <TabsTrigger value="queue" data-testid="tab-queue">
              <Package size={14} className="mr-1" /> Queue ({rows.length})
            </TabsTrigger>
            <TabsTrigger value="writeoffs" data-testid="tab-writeoffs">
              <Trash size={14} className="mr-1" /> Write-off log ({writeOffs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="queue" className="mt-4">
            {loading ? <p className="text-muted-foreground text-sm">Loading…</p>
             : rows.length === 0 ? (
              <div className="border-2 border-dashed border-border p-12 text-center">
                <CheckCircle size={32} weight="fill" className="mx-auto mb-2 text-emerald-600" />
                <p className="text-muted-foreground">Queue empty — no quarantine lots pending review.</p>
              </div>
             ) : (
              <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted border-b border-border">
                    <tr className="text-left text-[10px] font-bold tracking-wider uppercase text-muted-foreground">
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="quar-select-all" />
                      </th>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Source RMA</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const sel = selectedIds.has(r.inventory_id);
                      return (
                        <tr key={r.inventory_id} onClick={() => toggleOne(r.inventory_id)}
                          className={`border-b border-border cursor-pointer ${sel ? 'bg-blue-50' : 'hover:bg-muted'}`}
                          data-testid={`quar-row-${r.inventory_id}`}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={sel} onChange={() => toggleOne(r.inventory_id)} onClick={e => e.stopPropagation()} data-testid={`quar-cb-${r.inventory_id}`} />
                          </td>
                          <td className="px-3 py-2">
                            <p className="font-mono font-bold">{r.sku_code || r.variant_id?.slice(-8)}</p>
                            <p className="text-[11px] text-muted-foreground">{r.sku_name || r.variant_name || ''}</p>
                          </td>
                          <td className="px-3 py-2 font-mono text-muted-foreground">{r.location_code || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-900 font-mono font-bold">{r.quantity}</span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {r.last_rma_number ? <span className="text-brand">{r.last_rma_number}</span> : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{r.last_rma_reason || '—'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {r.received_at ? new Date(r.received_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
             )}
          </TabsContent>

          <TabsContent value="writeoffs" className="mt-4">
            {writeOffs.length === 0 ? (
              <p className="text-muted-foreground text-sm">No write-offs yet.</p>
            ) : (
              <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted border-b border-border">
                    <tr className="text-left text-[10px] font-bold tracking-wider uppercase text-muted-foreground">
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Variant</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {writeOffs.map((w, i) => (
                      <tr key={w.id || i} className="border-b border-border" data-testid={`writeoff-row-${i}`}>
                        <td className="px-3 py-2 text-muted-foreground text-xs">{new Date(w.written_off_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-mono text-xs">{w.variant_id?.slice(-10)}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-red-700">-{w.quantity}</td>
                        <td className="px-3 py-2">{w.reason}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{w.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <DialogContent data-testid="quar-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmAction === 'approve_for_sale' ? (
                <><CheckCircle size={20} className="text-emerald-700" weight="fill" /> Approve for sale</>
              ) : (
                <><Trash size={20} className="text-red-700" weight="fill" /> Write off</>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'approve_for_sale' ? (
                <>Move <b className="text-foreground">{selectedQty}</b> unit(s) across <b>{selectedIds.size}</b> lot(s) from <b>quarantine</b> back to <b className="text-emerald-700">available</b>.</>
              ) : (
                <>Permanently write off <b className="text-foreground">{selectedQty}</b> unit(s) across <b>{selectedIds.size}</b> lot(s). A write-off movement will be logged for audit.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">
              {confirmAction === 'write_off' ? 'Reason (required for audit)' : 'Note (optional)'}
            </Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
              placeholder={confirmAction === 'write_off' ? 'Damaged beyond repair · expired · etc.' : 'Passed visual inspection…'}
              data-testid="quar-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} data-testid="quar-cancel-btn">Cancel</Button>
            <Button onClick={runAction} disabled={busy || (confirmAction === 'write_off' && !note.trim())}
              data-testid="quar-confirm-btn"
              className={confirmAction === 'approve_for_sale' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-red-700 hover:bg-red-800'}>
              {busy ? 'Working…' : confirmAction === 'approve_for_sale' ? 'Approve' : 'Write off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
